import { useCallback, useState } from 'react';
import { IDismissNotificationProps, INotificationApiConfig } from '../models';

export default function useDismissNotification(
  props: IDismissNotificationProps & INotificationApiConfig
) {
  const { apiUrl, authToken, coreApiToken, graphToken, userId, handleDismiss } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const dismiss = useCallback(
    async (notificationIds: string[]) => {
      if (!userId) {
        setError(new Error('A user ID is required to dismiss notifications'));
        return false;
      }

      const userNotificationDetails = {
        userID: userId,
        notificationIDs: notificationIds,
        isSystemNotification: true,
      };

      try {
        setIsSubmitting(true);
        setError(null);
        await dismissNotification(userNotificationDetails);
        handleDismiss(notificationIds);
        return true;
      } catch (error) {
        setError(error instanceof Error ? error : new Error('Failed to dismiss notifications'));
        return false;
      } finally {
        setIsSubmitting(false);
      }

      async function dismissNotification(userNotificationDetails: {
        userID: string;
        notificationIDs: string[];
        isSystemNotification: boolean;
      }) {
        const response = await fetch(`${apiUrl}`, {
          method: 'POST',
          headers: {
            Authorization: authToken || '',
            Coreapitoken: coreApiToken || '',
            Graphtoken: graphToken,
            'Content-type': 'application/json;',
          },
          body: JSON.stringify(userNotificationDetails),
        });
        if (!response.ok) {
          throw new Error(`HTTP error. status: ${response.status}`);
        }

        const isDismissed = await response.json();
        if (isDismissed !== true) {
          throw new Error('Notification dismissal was not persisted');
        }
      }
    },
    [apiUrl, authToken, coreApiToken, graphToken, handleDismiss, userId]
  );

  return { isSubmitting, error, dismiss };
}

// Generated by GitHub Copilot | Updated on: 2026-10-05 | 10 lines modified | Validate dismissal API result
// Generated by GitHub Copilot | Updated on: 2026-10-05 | 24 lines modified | Return dismissal success state
